import {
	Component,
	CSSProperties,
	HTMLAttributes,
	ImgHTMLAttributes,
	ReactElement,
	ReactNode,
	SyntheticEvent,
} from "react"
// oxlint-disable-next-line import/no-named-as-default
import LazyLoadComponent, {
	DelayMethod,
	ScrollPosition,
} from "./LazyLoadComponent"

export type LazyLoadImageProps = Omit<
	ImgHTMLAttributes<HTMLImageElement>,
	"placeholder" | "onLoad"
> & {
	afterLoad?: () => void
	beforeLoad?: () => void
	delayMethod?: DelayMethod
	delayTime?: number
	effect?: string
	height?: number | string
	onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void
	placeholder?: ReactElement | null
	placeholderSrc?: string | null
	scrollPosition?: ScrollPosition
	style?: CSSProperties
	threshold?: number
	useIntersectionObserver?: boolean
	visibleByDefault?: boolean
	width?: number | string
	wrapperClassName?: string
	wrapperProps?: HTMLAttributes<HTMLSpanElement>
}

export class LazyLoadImage extends Component<
	LazyLoadImageProps,
	{ loaded: boolean }
> {
	static defaultProps: Partial<LazyLoadImageProps> = {
		onLoad: () => undefined,
		afterLoad: () => ({}),
		beforeLoad: () => ({}),
		delayMethod: "throttle",
		delayTime: 300,
		effect: "",
		placeholderSrc: null,
		threshold: 100,
		useIntersectionObserver: true,
		visibleByDefault: false,
		wrapperClassName: "",
	}

	constructor(props: LazyLoadImageProps) {
		super(props)
		this.state = { loaded: false }
	}

	onImageLoad() {
		if (this.state.loaded) {
			return null
		}
		return (e: SyntheticEvent<HTMLImageElement>) => {
			this.props.onLoad?.(e)
			this.props.afterLoad?.()
			this.setState({ loaded: true })
		}
	}

	getImg() {
		const {
			afterLoad,
			beforeLoad,
			delayMethod,
			delayTime,
			effect,
			placeholder,
			placeholderSrc,
			scrollPosition,
			threshold,
			useIntersectionObserver,
			visibleByDefault,
			wrapperClassName,
			wrapperProps,
			...imgProps
		} = this.props
		void afterLoad
		void beforeLoad
		void delayMethod
		void delayTime
		void effect
		void placeholder
		void placeholderSrc
		void scrollPosition
		void threshold
		void useIntersectionObserver
		void visibleByDefault
		void wrapperClassName
		void wrapperProps
		const onLoad = this.onImageLoad()
		return <img alt="" {...imgProps} onLoad={onLoad ?? undefined} />
	}

	getLazyLoadImage() {
		const {
			beforeLoad,
			className,
			delayMethod,
			delayTime,
			height,
			placeholder,
			scrollPosition,
			style,
			threshold,
			useIntersectionObserver,
			visibleByDefault,
			width,
		} = this.props
		return (
			<LazyLoadComponent
				beforeLoad={beforeLoad}
				className={className}
				delayMethod={delayMethod}
				delayTime={delayTime}
				height={height}
				placeholder={placeholder}
				scrollPosition={scrollPosition}
				style={style}
				threshold={threshold}
				useIntersectionObserver={useIntersectionObserver}
				visibleByDefault={visibleByDefault}
				width={width}
			>
				{this.getImg()}
			</LazyLoadComponent>
		)
	}

	getWrappedLazyLoadImage(lazyLoadImage: ReactNode) {
		const {
			effect,
			height,
			placeholderSrc,
			width,
			wrapperClassName,
			wrapperProps,
		} = this.props
		const { loaded } = this.state
		const wrapperBackground: CSSProperties =
			loaded || !placeholderSrc
				? {}
				: {
						backgroundImage: `url(${placeholderSrc})`,
						backgroundSize: "100% 100%",
					}
		const className = [
			wrapperClassName,
			"lazy-load-image-background",
			effect,
			loaded && "lazy-load-image-loaded",
		]
			.filter(Boolean)
			.join(" ")
		return (
			<span
				className={className}
				style={{
					...wrapperBackground,
					color: "transparent",
					display: "inline-block",
					height: height,
					width: width,
				}}
				{...wrapperProps}
			>
				{lazyLoadImage}
			</span>
		)
	}

	render() {
		const {
			effect,
			placeholderSrc,
			visibleByDefault,
			wrapperClassName,
			wrapperProps,
		} = this.props
		const lazyLoadImage = this.getLazyLoadImage()
		// oxlint-disable-next-line typescript/prefer-nullish-coalescing
		const needsWrapper = (effect || placeholderSrc) && !visibleByDefault
		if (!needsWrapper && !wrapperClassName && !wrapperProps) {
			return lazyLoadImage
		}
		return this.getWrappedLazyLoadImage(lazyLoadImage)
	}
}

export default LazyLoadImage
